<ng-container *transloco="let t">
  <div class="table-card" [class.table-card--filled]="styles().filled">
    <div
      class="table-wrapper"
      dssLibsUiAutoHideScrollbar
      [class.table-wrapper--rounded-top]="styles().roundedTop"
      [class.table-wrapper--rounded-bottom]="styles().roundedBottom"
      [class.table-wrapper--joined]="!styles().roundedBottom"
    >
      <mat-table
        [dataSource]="data()"
        matSort
        [matSortActive]="sortColumn() ?? ''"
        [matSortDirection]="sortDir() ?? ''"
        [style.min-width]="tableMinWidth()"
        (matSortChange)="sortChanged($event)"
        multiTemplateDataRows
      >
        <!-- SELECTION COLUMN -->
        @if (selectedRows()) {
        <ng-container matColumnDef="select">
          <mat-header-cell *matHeaderCellDef class="selection-cell">
            <mat-checkbox
              [checked]="isCurrentPageSelected()"
              [indeterminate]="isCurrentPagePartiallySelected()"
              [attr.aria-label]="t('TABLE.SELECT_PAGE')"
              (change)="toggleCurrentPageRows($event)"
            ></mat-checkbox>
          </mat-header-cell>

          <mat-cell *matCellDef="let row" class="selection-cell">
            <mat-checkbox
              [checked]="isRowSelected(row)"
              [attr.aria-label]="t('TABLE.SELECT_ROW')"
              (click)="$event.stopPropagation()"
              (change)="toggleRowSelection($event, row)"
            ></mat-checkbox>
          </mat-cell>

          @if (displayFooter()) {
          <mat-footer-cell *matFooterCellDef class="selection-cell"></mat-footer-cell>
          }
        </ng-container>
        }

        <!-- ROW EXPANSION COLUMN -->
        @if (expandedDetails()) {
        <ng-container matColumnDef="expand">
          <mat-header-cell *matHeaderCellDef class="expand-cell"></mat-header-cell>

          <mat-cell *matCellDef="let row" class="expand-cell">
            <button
              mat-icon-button
              class="expand-button"
              [class.expand-button--expanded]="expandedRow() === row"
              [attr.aria-label]="expandedRow() === row ? t('TABLE.COLLAPSE_ROW') : t('TABLE.EXPAND_ROW')"
              [attr.aria-expanded]="expandedRow() === row"
              (click)="toggleRowExpansion(row); $event.stopPropagation()"
            >
              <mat-icon svgIcon="chevron-down" class="size-4!"></mat-icon>
            </button>
          </mat-cell>

          @if (displayFooter()) {
          <mat-footer-cell *matFooterCellDef class="expand-cell"></mat-footer-cell>
          }
        </ng-container>
        }

        <!-- DATA COLUMNS -->
        @for (column of columns(); track column.id) {
        <ng-container [matColumnDef]="column.id">
          <mat-header-cell
            *matHeaderCellDef
            [style.min-width]="column.minWidth ?? null"
            [mat-sort-header]="column.id"
            [disabled]="!column.sortable"
          >
            {{ t(column.label) }}
          </mat-header-cell>

          <mat-cell
            *matCellDef="let row"
            [style.min-width]="column.minWidth ?? null"
            [class]="(row | renderFunction : column.cellClassFn) ?? ''"
          >
            <div
              class="cell-content"
              [class.truncate]="!column.component && !column.renderFn"
              [matTooltip]="(row | renderFunction : column.tooltipFn) ?? ''"
              [matTooltipDisabled]="!column.tooltipFn"
              [matTooltipShowDelay]="500"
              matTooltipPosition="after"
            >
              @if (column.component) {
              <ng-container
                *ngComponentOutlet="
                  column.component.type;
                  inputs: column.component.inputFn ? column.component.inputFn(row) : undefined
                "
              ></ng-container>
              } @else if (column.renderFn) {
              <div [innerHTML]="(row | renderFunction : column.renderFn) ?? '---'"></div>
              } @else {
              <span>{{ cellValue(row, column) }}</span>
              }
            </div>
          </mat-cell>

          @if (displayFooter()) {
          <mat-footer-cell *matFooterCellDef [style.min-width]="column.minWidth ?? null">
            @if (column.footerFn) {
            {{ column.footerFn(data()) }}
            }
          </mat-footer-cell>
          }
        </ng-container>
        }

        <!-- BUTTON ACTIONS COLUMN -->
        @if (buttonActions().length) {
        <ng-container matColumnDef="button-actions">
          <mat-header-cell *matHeaderCellDef class="actions-cell" [style.flex]="buttonActionsFlex()"></mat-header-cell>

          <mat-cell
            *matCellDef="let row"
            class="actions-cell"
            [style.flex]="buttonActionsFlex()"
            (click)="$event.stopPropagation()"
          >
            @for (buttonAction of buttonActions(); track buttonAction.action) { @if (!(row | conditionFunction :
            buttonAction.hiddenFn)) { @if (buttonAction.variant === 'text') {
            <dss-libs-ui-button
              [label]="buttonAction.label"
              [disabled]="buttonAction.disabled || (row | conditionFunction : buttonAction.disabledFn)"
              [appliedClasses]="buttonAction.appliedClasses ?? ''"
              (clickOutput)="actionClicked(buttonAction.action, row)"
            ></dss-libs-ui-button>
            } @else {
            <button
              mat-icon-button
              [disabled]="buttonAction.disabled || (row | conditionFunction : buttonAction.disabledFn)"
              [class]="'action-button--' + (buttonAction.color ?? 'primary') + ' flex! items-center justify-center'"
              [matTooltip]="t(buttonAction.label)"
              [matTooltipShowDelay]="800"
              [attr.aria-label]="t(buttonAction.label)"
              (click)="actionClicked(buttonAction.action, row)"
            >
              @if (buttonAction.iconMetaData) {
              <mat-icon
                [svgIcon]="buttonAction.iconMetaData.icon"
                [class]="buttonAction.iconMetaData.appliedClasses ?? ''"
              ></mat-icon>
              }
            </button>
            } } }
          </mat-cell>

          @if (displayFooter()) {
          <mat-footer-cell *matFooterCellDef class="actions-cell" [style.flex]="buttonActionsFlex()"></mat-footer-cell>
          }
        </ng-container>
        }

        <!-- MENU ACTIONS COLUMN -->
        @if (menuActions().length) {
        <ng-container matColumnDef="menu-actions">
          <mat-header-cell *matHeaderCellDef class="actions-cell"></mat-header-cell>

          <mat-cell *matCellDef="let row" class="actions-cell" (click)="$event.stopPropagation()">
            <button
              type="button"
              class="menu-trigger"
              [matMenuTriggerFor]="menu.matMenu()"
              [attr.aria-label]="t('TABLE.ACTIONS')"
              (click)="activeRow.set(row)"
            >
              <mat-icon svgIcon="options"></mat-icon>
            </button>
          </mat-cell>

          @if (displayFooter()) {
          <mat-footer-cell *matFooterCellDef class="actions-cell"></mat-footer-cell>
          }
        </ng-container>
        }

        <!-- EXPANDED DETAILS COLUMN -->
        @if (expandedDetails()) {
        <ng-container matColumnDef="expandedDetail">
          <mat-cell *matCellDef="let row" class="expanded-details-cell">
            <div
              class="expanded-details-wrapper"
              [style.grid-template-rows]="
                ((expandedRow() === row ? 1 : (row | renderFunction : initialExpansionFn())) ?? 0) + 'fr'
              "
            >
              <div class="flex min-h-0">
                @if (expandedDetailsTemplate()) {
                <ng-container
                  *ngTemplateOutlet="expandedDetailsTemplate()!; context: { $implicit: row }"
                ></ng-container>
                } @else if (expandedDetailsComponent(); as detailsComponent) {
                <ng-container
                  *ngComponentOutlet="
                    detailsComponent.type;
                    inputs: detailsComponent.inputFn ? detailsComponent.inputFn(row) : undefined
                  "
                ></ng-container>
                }
              </div>
            </div>
          </mat-cell>
        </ng-container>
        } @if (showHeader()) {
        <mat-header-row *matHeaderRowDef="displayedColumns()"></mat-header-row>
        }

        <mat-row
          *matRowDef="let row; columns: displayedColumns()"
          class="transition-colors"
          [class.clickable-row]="isRowClickable()"
          [attr.tabindex]="isRowClickable() ? 0 : null"
          (click)="rowActivated(row)"
          (keydown.enter)="rowActivated(row)"
          (keydown.space)="rowActivated(row); $event.preventDefault()"
        ></mat-row>

        @if (expandedDetails()) {
        <mat-row *matRowDef="let row; columns: ['expandedDetail']" class="expanded-details-row"></mat-row>
        }

        <div class="empty-row" *matNoDataRow>
          <mat-icon svgIcon="documents-outline" class="size-8!"></mat-icon>
          <p class="text-sm text-ink-muted">{{ t('TABLE.NO_DATA') }}</p>
        </div>

        @if (displayFooter()) {
        <mat-footer-row *matFooterRowDef="displayedColumns()"></mat-footer-row>
        }
      </mat-table>
    </div>
  </div>

  <dss-libs-ui-menu #menu [menuActions]="rowMenuActions()" (actionOutput)="actionClicked($event)"></dss-libs-ui-menu>
</ng-container>
